import React, { useEffect, useMemo, useRef, useState } from "react";
import LabeledInput from "../components/LabeledInput";
import { useAuthBootstrap } from "../../../context/AuthBootstrapContext";
import "./creativeProfile.css";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL || "https://api-2026.fikacreatives.co";
const CREATIVE_PROFILE_ENDPOINT = `${API_BASE}/api/settings/creative-profile`;

async function readJsonSafely(res) {
  const contentType = res.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    return res.json();
  }

  const text = await res.text();
  throw new Error(text || "Unexpected server response");
}

function normalizeEmptyString(value) {
  const next = String(value || "").trim();
  return next ? next : null;
}

function toExternalUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  try {
    return new URL(raw).toString();
  } catch {
    try {
      return new URL(`https://${raw}`).toString();
    } catch {
      return "";
    }
  }
}

async function createSignedUpload(kind, file) {
  const res = await fetch(CREATIVE_PROFILE_ENDPOINT, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      action: "create_upload_url",
      file_kind: kind,
      file_name: file?.name || null,
      content_type: file?.type || "application/octet-stream",
    }),
  });

  const data = await readJsonSafely(res);

  if (!res.ok || !data?.ok) {
    throw new Error(data?.error || `Could not prepare ${kind} upload`);
  }

  if (!data?.signedUrl) {
    throw new Error(`Upload prep for ${kind} did not return a signedUrl`);
  }

  return data;
}

async function uploadFileDirectToStorage(kind, file) {
  if (!file) return null;

  const prep = await createSignedUpload(kind, file);

  const uploadRes = await fetch(prep.signedUrl, {
    method: "PUT",
    headers: {
      "Content-Type": file.type || "application/octet-stream",
    },
    body: file,
  });

  if (!uploadRes.ok) {
    const text = await uploadRes.text();
    throw new Error(text || `Upload failed for ${kind}`);
  }

  return {
    storagePath: prep.storagePath,
    publicUrl: prep.publicUrl,
    bucket: prep.bucket,
  };
}

async function saveCreativeProfile(payload) {
  const res = await fetch(CREATIVE_PROFILE_ENDPOINT, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const data = await readJsonSafely(res);

  if (!res.ok || !data?.ok) {
    throw new Error(data?.error || "Failed to save creative profile");
  }

  return data;
}

export default function CreativeProfileSection() {
  const avatarInputRef = useRef(null);
  const projectInputRef = useRef(null);

  const {
    person,
    profileAssets,
    setProfileAssets,
    refresh,
    refreshBootstrap,
  } = useAuthBootstrap();

  const [linkedin, setLinkedin] = useState("");
  const [portfolio, setPortfolio] = useState("");
  const [projectInMind, setProjectInMind] = useState("");
  const [coffeeInvitesEnabled, setCoffeeInvitesEnabled] = useState(false);

  const [avatarFile, setAvatarFile] = useState(null);
  const [projectImageFile, setProjectImageFile] = useState(null);

  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState(null);
  const [projectPreviewUrl, setProjectPreviewUrl] = useState(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const avatarUrl = avatarPreviewUrl || profileAssets?.profile_image_url || "";
  const projectImageUrl =
    projectPreviewUrl || profileAssets?.project_image_url || "";

  const linkedinHref = useMemo(() => toExternalUrl(linkedin), [linkedin]);
  const portfolioHref = useMemo(() => toExternalUrl(portfolio), [portfolio]);

  useEffect(() => {
    setLinkedin(person?.linkedin_handle || "");
    setPortfolio(person?.portfolio || "");
    setCoffeeInvitesEnabled(Boolean(person?.coffee_invites_enabled));
  }, [person?.linkedin_handle, person?.portfolio, person?.coffee_invites_enabled]);

  useEffect(() => {
    setProjectInMind(profileAssets?.primary_project_description || "");
  }, [profileAssets?.primary_project_description]);

  useEffect(() => {
    return () => {
      if (avatarPreviewUrl) URL.revokeObjectURL(avatarPreviewUrl);
      if (projectPreviewUrl) URL.revokeObjectURL(projectPreviewUrl);
    };
  }, [avatarPreviewUrl, projectPreviewUrl]);

  function onPickAvatar() {
    avatarInputRef.current?.click();
  }

  function onPickProjectImage() {
    projectInputRef.current?.click();
  }

  function onAvatarChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (avatarPreviewUrl) {
      URL.revokeObjectURL(avatarPreviewUrl);
    }

    const preview = URL.createObjectURL(file);
    setAvatarFile(file);
    setAvatarPreviewUrl(preview);
    setSuccess("");
    setError("");
  }

  function onProjectImageChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (projectPreviewUrl) {
      URL.revokeObjectURL(projectPreviewUrl);
    }

    const preview = URL.createObjectURL(file);
    setProjectImageFile(file);
    setProjectPreviewUrl(preview);
    setSuccess("");
    setError("");
  }

  async function onSave(e) {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      let finalAvatarUrl = profileAssets?.profile_image_url || null;
      let finalProjectImageUrl = profileAssets?.project_image_url || null;

      if (avatarFile) {
        const uploadedAvatar = await uploadFileDirectToStorage(
          "profile",
          avatarFile
        );
        finalAvatarUrl = uploadedAvatar?.publicUrl || null;
      }

      if (projectImageFile) {
        const uploadedProject = await uploadFileDirectToStorage(
          "project",
          projectImageFile
        );
        finalProjectImageUrl = uploadedProject?.publicUrl || null;
      }

      const payload = {
        linkedin_handle: normalizeEmptyString(linkedin),
        portfolio: normalizeEmptyString(portfolio),
        profile_image_url: finalAvatarUrl,
        project_image_url: finalProjectImageUrl,
        primary_project_description: normalizeEmptyString(projectInMind),
        coffee_invites_enabled: coffeeInvitesEnabled,
      };

      const data = await saveCreativeProfile(payload);
      const creativeProfile = data?.creativeProfile || {};

      const nextProfileAssets = {
        ...(profileAssets || {}),
        ...creativeProfile,
        profile_image_url:
          creativeProfile?.profile_image_url ?? finalAvatarUrl ?? null,
        project_image_url:
          creativeProfile?.project_image_url ?? finalProjectImageUrl ?? null,
      };

      setProfileAssets?.(nextProfileAssets);

      setLinkedin(
        creativeProfile?.linkedin_handle ?? payload.linkedin_handle ?? ""
      );
      setPortfolio(creativeProfile?.portfolio ?? payload.portfolio ?? "");
      setProjectInMind(
        creativeProfile?.primary_project_description ?? payload.primary_project_description ?? ""
      );
      setCoffeeInvitesEnabled(
        typeof creativeProfile?.coffee_invites_enabled === "boolean"
          ? creativeProfile.coffee_invites_enabled
          : coffeeInvitesEnabled
      );

      setAvatarFile(null);
      setProjectImageFile(null);

      if (avatarInputRef.current) {
        avatarInputRef.current.value = "";
      }

      if (projectInputRef.current) {
        projectInputRef.current.value = "";
      }

      if (avatarPreviewUrl) {
        URL.revokeObjectURL(avatarPreviewUrl);
        setAvatarPreviewUrl(null);
      }

      if (projectPreviewUrl) {
        URL.revokeObjectURL(projectPreviewUrl);
        setProjectPreviewUrl(null);
      }

      if (typeof refreshBootstrap === "function") {
        await refreshBootstrap();
      } else if (typeof refresh === "function") {
        await refresh();
      }

      setSuccess("Creative profile updated.");
    } catch (err) {
      setError(err?.message || "Could not save changes.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="cp" onSubmit={onSave}>
      <div className="cp__top">
        <div className="cp__avatarCol">
          <button
            type="button"
            className="cp__avatar"
            onClick={onPickAvatar}
            aria-label="Upload profile image"
          >
            {avatarUrl ? (
              <img className="cp__avatarImg" src={avatarUrl} alt="Profile" />
            ) : (
              <div className="cp__avatarPlaceholder" />
            )}
          </button>

          <input
            ref={avatarInputRef}
            type="file"
            accept="image/*"
            className="cp__file"
            onChange={onAvatarChange}
          />
        </div>

        <div className="cp__fields">
          <div className="cp__grid2">
            <div>
              <LabeledInput
                label="LinkedIn"
                name="linkedin"
                value={linkedin}
                onChange={setLinkedin}
                placeholder="Add your LinkedIn URL"
                autoComplete="url"
              />
              {linkedinHref ? (
                <a
                  className="cp__linkPreview"
                  href={linkedinHref}
                  target="_blank"
                  rel="noreferrer"
                >
                  View LinkedIn
                </a>
              ) : null}
            </div>

            <div>
              <LabeledInput
                label="Portfolio"
                name="portfolio"
                value={portfolio}
                onChange={setPortfolio}
                placeholder="Add your portfolio URL"
                autoComplete="url"
              />
              {portfolioHref ? (
                <a
                  className="cp__linkPreview"
                  href={portfolioHref}
                  target="_blank"
                  rel="noreferrer"
                >
                  View Portfolio
                </a>
              ) : null}
            </div>
          </div>

          <div className="cp__sectionLabel">Primary Project</div>

          <button
            type="button"
            className="cp__projectCard"
            onClick={onPickProjectImage}
            aria-label="Upload primary project image"
          >
            <div className="cp__projectInner">
              {projectImageUrl ? (
                <img
                  className="cp__projectImg"
                  src={projectImageUrl}
                  alt="Primary project"
                />
              ) : (
                <div className="cp__projectEmpty">
                  <div className="cp__projectHint">Click to upload</div>
                </div>
              )}
            </div>
          </button>

          <input
            ref={projectInputRef}
            type="file"
            accept="image/*"
            className="cp__file"
            onChange={onProjectImageChange}
          />

          <div className="cp__sectionLabel" style={{ marginTop: 16 }}>Project in mind</div>
          <textarea
            className="cp__textarea"
            value={projectInMind}
            onChange={(e) => { setProjectInMind(e.target.value); setSuccess(""); setError(""); }}
            placeholder="What do you want to bring to life?"
            rows={3}
          />
        </div>
      </div>

      <div className="cp__sectionLabel" style={{ marginTop: 8 }}>Coffee Invites</div>
      <div className="cp__toggleRow">
        <label className="cp__toggleSwitch">
          <input
            type="checkbox"
            className="cp__toggleInput"
            checked={coffeeInvitesEnabled}
            onChange={(e) => { setCoffeeInvitesEnabled(e.target.checked); setSuccess(""); setError(""); }}
          />
          <span className="cp__toggleTrack" />
        </label>
        <div className="cp__toggleBody">
          <span className="cp__toggleLabel">Share my number for coffee meetups</span>
          <p className="cp__toggleDesc">
            When enabled, other members can send you a coffee invite. We'll share your phone number so you can coordinate a time and place to meet up.
          </p>
        </div>
      </div>

      {error ? <p className="cp__error">{error}</p> : null}
      {success ? <p className="cp__success">{success}</p> : null}

      <div className="cp__actions">
        <button className="btnPrimary" type="submit" disabled={saving}>
          {saving ? "Saving..." : "Save changes"}
        </button>
      </div>
    </form>
  );
}